import type { Person, Relationship } from './db';
import { newId } from './ids';

// GEDCOM 5.5.1 import/export. Accepts the common variants we see in the wild
// (BOM, CRLF, level-2+ CONC/CONT continuations, lower-case month tokens,
// xref tokens with internal dots/digits like @P1.1@). Hardened on 2026-05-06
// after Mike's "import did nothing" feedback to surface failures clearly.

interface GedLine {
  level: number;
  tag: string;
  xref?: string;
  pointer?: string;     // value when it's a pointer like '@F1@'
  value?: string;
  raw: string;
  lineNo: number;
}

function stripBom(raw: string): string {
  return raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
}

function parseLines(raw: string): GedLine[] {
  const lines = stripBom(raw).replace(/\r\n?/g, '\n').split('\n');
  const out: GedLine[] = [];
  // Permissive line regex:
  //   level (digits) | optional xref @...@ | tag (letters/digits/_) | optional value
  // We allow xrefs to contain anything except '@' (per spec). Also accept the
  // less-common but valid @VOID@ pointer form.
  const re = /^\s*(\d+)\s+(?:(@[^@]+@)\s+)?([A-Za-z_0-9]+)(?:\s(.*))?$/;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (!l.trim()) continue;
    const m = re.exec(l);
    if (!m) continue;
    const level = Number(m[1]);
    const xref = m[2];
    const tag = m[3].toUpperCase();
    let value = m[4]?.trimEnd();
    // Detect pointer values like '@F1@' (used by HUSB/WIFE/CHIL/FAMC/FAMS).
    let pointer: string | undefined;
    if (value && /^@[^@]+@$/.test(value.trim())) {
      pointer = value.trim();
      value = undefined;
    }
    out.push({ level, xref, tag, value, pointer, raw: l, lineNo: i + 1 });
  }
  return out;
}

const MONTHS: Record<string, string> = {
  JAN:'01',FEB:'02',MAR:'03',APR:'04',MAY:'05',JUN:'06',
  JUL:'07',AUG:'08',SEP:'09',OCT:'10',NOV:'11',DEC:'12',
};

function gedDateToIso(s?: string): string | undefined {
  if (!s) return undefined;
  // Strip qualifier prefixes (ABT/BEF/AFT/EST/CAL/INT) and FROM/TO ranges
  // (we keep just the first date in a range).
  let cleaned = s.trim();
  cleaned = cleaned.replace(/^(ABT|BEF|AFT|EST|CAL|INT|FROM)\s+/i, '');
  cleaned = cleaned.replace(/\s+TO\s+.*$/i, '');
  cleaned = cleaned.trim();
  const m1 = /^(\d{1,2})\s+(\w{3,9})\s+(\d{4})$/i.exec(cleaned);
  const m2 = /^(\w{3,9})\s+(\d{4})$/i.exec(cleaned);
  const m3 = /^(\d{4})$/.exec(cleaned);
  const m4 = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(cleaned);  // already-iso
  if (m4) return `${m4[1]}-${m4[2].padStart(2,'0')}-${m4[3].padStart(2,'0')}`;
  const monthFromTok = (tok: string) => MONTHS[tok.slice(0, 3).toUpperCase()];
  if (m1) {
    const mo = monthFromTok(m1[2]);
    if (!mo) return m1[3];
    return `${m1[3]}-${mo}-${m1[1].padStart(2, '0')}`;
  }
  if (m2) {
    const mo = monthFromTok(m2[1]);
    if (!mo) return m2[2];
    return `${m2[2]}-${mo}`;
  }
  if (m3) return m3[1];
  return undefined;
}

function isoToGedDate(iso?: string): string | undefined {
  if (!iso) return undefined;
  const m = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(iso);
  if (!m) return undefined;
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const y = m[1]; const mo = m[2]; const d = m[3];
  if (d && mo) return `${Number(d)} ${months[Number(mo) - 1]} ${y}`;
  if (mo) return `${months[Number(mo) - 1]} ${y}`;
  return y;
}

export interface GedcomParseResult {
  people: Person[];
  relationships: Relationship[];
  /** Diagnostics — surfaced in the UI when import yields 0 records. */
  warnings: string[];
  /** Cheap summary for logging. */
  stats: {
    totalLines: number;
    individualBlocks: number;
    familyBlocks: number;
    skippedXrefs: number;
  };
}

export function parseGedcom(raw: string, treeId: string): GedcomParseResult {
  const lines = parseLines(raw);
  const people = new Map<string, Person>();           // xref → Person
  const families = new Map<string, { husb?: string; wife?: string; children: string[] }>();
  const warnings: string[] = [];
  let individualBlocks = 0;
  let familyBlocks = 0;
  let skippedXrefs = 0;

  if (lines.length === 0) {
    warnings.push('No GEDCOM lines could be parsed. Is the file empty or in an unexpected format?');
    return {
      people: [], relationships: [], warnings,
      stats: { totalLines: 0, individualBlocks: 0, familyBlocks: 0, skippedXrefs: 0 },
    };
  }

  let i = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (l.level === 0 && l.xref && l.tag === 'INDI') {
      individualBlocks++;
      const person: Person = {
        id: newId(), treeId, name: '', createdAt: Date.now(), updatedAt: Date.now(),
      };
      i++;
      while (i < lines.length && lines[i].level > 0) {
        const sub = lines[i];
        if (sub.level === 1 && sub.tag === 'NAME' && sub.value) {
          person.name = sub.value.replace(/\//g, '').replace(/\s+/g, ' ').trim();
        } else if (sub.level === 1 && sub.tag === 'SEX' && sub.value) {
          const v = sub.value.trim().toUpperCase();
          person.gender = v === 'M' ? 'male' : v === 'F' ? 'female' : v === 'U' ? 'unknown' : 'other';
        } else if (sub.level === 1 && (sub.tag === 'BIRT' || sub.tag === 'DEAT')) {
          const kind = sub.tag;
          i++;
          while (i < lines.length && lines[i].level > 1) {
            const sub2 = lines[i];
            if (sub2.tag === 'DATE' && sub2.value) {
              const iso = gedDateToIso(sub2.value);
              if (kind === 'BIRT') person.dob = iso; else person.death = iso;
            } else if (sub2.tag === 'PLAC' && sub2.value) {
              if (kind === 'BIRT') person.birthPlace = sub2.value; else person.deathPlace = sub2.value;
            }
            i++;
          }
          continue;
        } else if (sub.level === 1 && sub.tag === 'NOTE' && (sub.value || sub.pointer)) {
          const head = sub.value ?? '';
          let buf = head;
          // Consume CONC (no leading space) / CONT (newline) continuation lines.
          let j = i + 1;
          while (j < lines.length && lines[j].level > 1) {
            const cont = lines[j];
            if (cont.tag === 'CONC' && cont.value) buf += cont.value;
            else if (cont.tag === 'CONT' && cont.value !== undefined) buf += '\n' + cont.value;
            else break;
            j++;
          }
          person.notes = (person.notes ? person.notes + '\n' : '') + buf;
          i = j;
          continue;
        } else if (sub.level === 1 && sub.tag === 'OCCU' && sub.value) {
          person.label = sub.value;
        }
        i++;
      }
      people.set(l.xref, person);
      continue;
    }

    if (l.level === 0 && l.xref && l.tag === 'FAM') {
      familyBlocks++;
      const fam: { husb?: string; wife?: string; children: string[] } = { children: [] };
      i++;
      while (i < lines.length && lines[i].level > 0) {
        const sub = lines[i];
        const ptr = sub.pointer || sub.value?.trim();
        if (sub.level === 1 && sub.tag === 'HUSB' && ptr) fam.husb = ptr;
        else if (sub.level === 1 && sub.tag === 'WIFE' && ptr) fam.wife = ptr;
        else if (sub.level === 1 && sub.tag === 'CHIL' && ptr) fam.children.push(ptr);
        i++;
      }
      families.set(l.xref, fam);
      continue;
    }

    i++;
  }

  // Diagnostic: if we found INDI blocks but no FAM blocks, that's still a valid
  // tree — many small GEDCOMs are individuals only. If we found neither, surface
  // a useful error.
  if (people.size === 0 && families.size === 0) {
    if (lines.some((l) => l.level === 0 && l.tag === 'INDI' && !l.xref)) {
      warnings.push(
        'Found INDI records but they had no xref ID (e.g. "0 INDI" instead of "0 @I1@ INDI"). ' +
        'This GEDCOM may be from a tool that uses a non-standard format.',
      );
    } else if (lines.length > 0) {
      warnings.push(
        `Parsed ${lines.length} GEDCOM lines but found no INDI (individual) or FAM (family) blocks. ` +
        'Open the file and check it starts with "0 HEAD" and contains records like "0 @I1@ INDI".',
      );
    } else {
      warnings.push('GEDCOM file appears empty or unparseable.');
    }
  }

  const relationships: Relationship[] = [];
  const now = Date.now();
  for (const fam of families.values()) {
    const h = fam.husb ? people.get(fam.husb)?.id : undefined;
    const w = fam.wife ? people.get(fam.wife)?.id : undefined;
    if (fam.husb && !h) skippedXrefs++;
    if (fam.wife && !w) skippedXrefs++;
    if (h && w) {
      const [a, b] = [h, w].sort();
      relationships.push({
        id: newId(), treeId, kind: 'spouse', a, b,
        createdAt: now, updatedAt: now,
      });
    }
    for (const c of fam.children) {
      const childId = people.get(c)?.id;
      if (!childId) { skippedXrefs++; continue; }
      for (const parent of [h, w]) {
        if (!parent) continue;
        relationships.push({
          id: newId(), treeId, kind: 'parent', a: parent, b: childId,
          createdAt: now, updatedAt: now,
        });
      }
    }
  }

  if (skippedXrefs > 0) {
    warnings.push(
      `Skipped ${skippedXrefs} family relationship reference(s) because the referenced person could not be found. ` +
      'This usually means the GEDCOM is partial or referenced individuals are in a separate file.',
    );
  }

  return {
    people: [...people.values()],
    relationships,
    warnings,
    stats: {
      totalLines: lines.length,
      individualBlocks,
      familyBlocks,
      skippedXrefs,
    },
  };
}

export function buildGedcom(people: Person[], rels: Relationship[]): string {
  const out: string[] = [];
  out.push('0 HEAD');
  out.push('1 SOUR LINSMART');
  out.push('1 GEDC');
  out.push('2 VERS 5.5.1');
  out.push('2 FORM LINEAGE-LINKED');
  out.push('1 CHAR UTF-8');

  const pidToXref = new Map<string, string>();
  people.forEach((p, idx) => pidToXref.set(p.id, `@I${idx + 1}@`));

  for (const p of people) {
    const x = pidToXref.get(p.id)!;
    out.push(`0 ${x} INDI`);
    out.push(`1 NAME ${formatName(p.name)}`);
    if (p.gender) out.push(`1 SEX ${p.gender === 'male' ? 'M' : p.gender === 'female' ? 'F' : 'U'}`);
    if (p.dob || p.birthPlace) {
      out.push('1 BIRT');
      if (p.dob) out.push(`2 DATE ${isoToGedDate(p.dob)}`);
      if (p.birthPlace) out.push(`2 PLAC ${p.birthPlace}`);
    }
    if (p.death || p.deathPlace) {
      out.push('1 DEAT');
      if (p.death) out.push(`2 DATE ${isoToGedDate(p.death)}`);
      if (p.deathPlace) out.push(`2 PLAC ${p.deathPlace}`);
    }
    if (p.notes) out.push(`1 NOTE ${p.notes.replace(/\n/g, ' ')}`);
  }

  // Group rels into families keyed by (spouseA, spouseB) if present, else parent pairs.
  type Fam = { husb?: string; wife?: string; children: Set<string> };
  const fams = new Map<string, Fam>();

  function famKey(a?: string, b?: string) { return [a ?? '', b ?? ''].sort().join('|'); }

  const parentsOfChild = new Map<string, string[]>();
  for (const r of rels) {
    if (r.kind === 'parent') {
      const arr = parentsOfChild.get(r.b) ?? [];
      arr.push(r.a);
      parentsOfChild.set(r.b, arr);
    }
  }
  for (const r of rels) {
    if (r.kind === 'spouse') {
      const key = famKey(r.a, r.b);
      if (!fams.has(key)) fams.set(key, { husb: r.a, wife: r.b, children: new Set() });
    }
  }
  for (const [child, parents] of parentsOfChild) {
    if (parents.length === 0) continue;
    const [a, b] = parents.slice(0, 2).sort();
    const key = famKey(a, b);
    if (!fams.has(key)) fams.set(key, { husb: a, wife: b, children: new Set() });
    fams.get(key)!.children.add(child);
  }

  let idx = 1;
  for (const fam of fams.values()) {
    const fx = `@F${idx++}@`;
    out.push(`0 ${fx} FAM`);
    if (fam.husb && pidToXref.has(fam.husb)) out.push(`1 HUSB ${pidToXref.get(fam.husb)}`);
    if (fam.wife && pidToXref.has(fam.wife)) out.push(`1 WIFE ${pidToXref.get(fam.wife)}`);
    for (const c of fam.children) if (pidToXref.has(c)) out.push(`1 CHIL ${pidToXref.get(c)}`);
  }

  out.push('0 TRLR');
  return out.join('\n') + '\n';
}

function formatName(full: string): string {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return `${parts[0]} //`;
  const given = parts.slice(0, -1).join(' ');
  const sur = parts[parts.length - 1];
  return `${given} /${sur}/`;
}
